import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  UserPlus, 
  Shield, 
  Users,
  Key,
  Copy,
  Check,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  AlertTriangle,
  Settings2,
  Link2,
  ArrowRight,
  Zap,
  BarChart3
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  { value: "super_admin", label: "Super Admin", color: "bg-emerald-100 text-emerald-800", description: "Complete system access, sees all leads" },
  { value: "admin", label: "Admin", color: "bg-red-100 text-red-800", description: "Manages team leads and their teams" },
  { value: "team_lead", label: "Team Lead", color: "bg-purple-100 text-purple-800", description: "Manages callers/agents under them" },
  { value: "caller", label: "Caller/Agent", color: "bg-blue-100 text-blue-800", description: "Handles assigned leads only" },
];

export default function UserManagementPage({ user, logout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRole, setMyRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  
  // Agent Mapping State
  const [agentMappings, setAgentMappings] = useState({ privyr_agents: [], system_users: [] });
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [applyingMappings, setApplyingMappings] = useState(false);
  
  // Allocation Stats State
  const [allocationStats, setAllocationStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Lead Sources State for source-based allocation
  const [leadSources, setLeadSources] = useState([]);
  const [editingSourcesUser, setEditingSourcesUser] = useState(null);
  
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "caller",
    password: ""
  });

  useEffect(() => {
    fetchMyRole();
    fetchUsers();
    fetchLeadSources();
  }, []);

  useEffect(() => {
    if (activeTab === "mapping") {
      fetchAgentMappings();
    } else if (activeTab === "allocation") {
      fetchAllocationStats();
    }
  }, [activeTab]);

  const fetchMyRole = async () => {
    try {
      const response = await axios.get(`${API}/team/my-role`, { withCredentials: true });
      setMyRole(response.data.user);
    } catch (error) {
      console.error("Failed to fetch role");
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/team/users`, { withCredentials: true });
      setUsers(response.data.users || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view users");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadSources = async () => {
    try {
      const response = await axios.get(`${API}/team/lead-sources`, { withCredentials: true });
      setLeadSources(response.data.sources || []);
    } catch (error) {
      console.error("Failed to fetch lead sources");
    }
  };

  const handleUpdateAllowedSources = async (userId, allowedSources) => {
    try {
      await axios.put(`${API}/team/users/${userId}/sources`, { allowed_sources: allowedSources }, { withCredentials: true });
      toast.success("Allowed sources updated");
      fetchUsers();
      setEditingSourcesUser(null);
    } catch (error) {
      toast.error("Failed to update allowed sources");
    }
  };

  const fetchAgentMappings = async () => {
    setLoadingMappings(true);
    try {
      const response = await axios.get(`${API}/team/agent-name-mapping`, { withCredentials: true });
      setAgentMappings(response.data);
    } catch (error) {
      toast.error("Failed to fetch agent mappings");
    } finally {
      setLoadingMappings(false);
    }
  };

  const fetchAllocationStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API}/team/allocation-stats`, { withCredentials: true });
      setAllocationStats(response.data);
    } catch (error) {
      console.error("Failed to fetch allocation stats");
    } finally {
      setLoadingStats(false);
    }
  };

  const handleMapAgent = async (privyrName, userId) => {
    try {
      await axios.post(`${API}/team/agent-name-mapping`, {
        privyr_name: privyrName,
        user_id: userId
      }, { withCredentials: true });
      toast.success(`Mapped "${privyrName}" successfully`);
      fetchAgentMappings();
    } catch (error) {
      toast.error("Failed to map agent");
    }
  };

  const handleApplyAllMappings = async () => {
    // Check if any mappings exist
    const hasMappings = agentMappings.privyr_agents?.some(a => a.mapped_to);
    if (!hasMappings) {
      toast.error("Please map at least one Privyr agent to a system user first");
      return;
    }
    
    setApplyingMappings(true);
    try {
      const response = await axios.post(`${API}/team/apply-agent-mappings`, {}, { withCredentials: true });
      if (response.data.updated === 0) {
        toast.info("No new leads to update - all mapped leads already assigned");
      } else {
        toast.success(`Updated ${response.data.updated} leads with agent assignments`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply mappings");
    } finally {
      setApplyingMappings(false);
    }
  };

  const handleUpdateAllocationSettings = async (userId, settings) => {
    try {
      await axios.put(`${API}/team/users/${userId}/allocation`, settings, { withCredentials: true });
      toast.success("Allocation settings updated");
      fetchUsers();
      fetchAllocationStats();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error("Name and email are required");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/users`, {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        password: newUser.password || null
      }, { withCredentials: true });
      
      if (response.data.temp_password) {
        setTempPassword(response.data.temp_password);
        setSelectedUser({ email: newUser.email, name: newUser.name });
        setShowPasswordModal(true);
      }
      
      toast.success("User created successfully!");
      setShowCreateModal(false);
      setNewUser({ name: "", email: "", role: "caller", password: "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await axios.post(`${API}/auth/reset-password/${userId}`, {}, { withCredentials: true });
      setTempPassword(response.data.temp_password);
      setSelectedUser({ email: response.data.user_email });
      setShowPasswordModal(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleToggleStatus = async (userId, isActive) => {
    try {
      await axios.put(`${API}/team/users/${userId}`, { is_active: !isActive }, { withCredentials: true });
      toast.success(isActive ? "User deactivated" : "User activated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/team/users/${userId}`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
    toast.success("Password copied to clipboard!");
  };

  const getRoleColor = (role) => {
    const r = roles.find(r => r.value === role);
    return r?.color || "bg-gray-100 text-gray-800";
  };

  if (!["admin", "super_admin"].includes(myRole?.role)) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-900">Access Restricted</h2>
            <p className="text-slate-500 mt-2">Only administrators can manage users</p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">User Management</h1>
            <p className="text-slate-600 mt-1">Manage users, agent mappings, and lead allocation</p>
          </div>
          <Button 
            className="bg-cyan-700 hover:bg-cyan-800"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="mapping" className="flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Agent Mapping
            </TabsTrigger>
            <TabsTrigger value="allocation" className="flex items-center gap-2">
              <Settings2 className="w-4 h-4" />
              Allocation
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {/* Role Legend */}
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Role Permissions:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {roles.map((role) => (
                    <div key={role.value} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Badge className={role.color}>{role.label}</Badge>
                      <span className="text-sm text-slate-600">{role.description}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>All Users ({users.length})</span>
                  <Button variant="ghost" size="sm" onClick={fetchUsers}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No users found</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Reports To</TableHead>
                        <TableHead>Allowed Sources</TableHead>
                        <TableHead>Active for Allocation</TableHead>
                        <TableHead>Max Leads/Day</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.user_id} className={u.is_active === false ? "opacity-50" : ""}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={u.picture} />
                                <AvatarFallback className="bg-cyan-100 text-cyan-700 text-sm">
                                  {u.name?.charAt(0) || "U"}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{u.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600">{u.email}</TableCell>
                          <TableCell>
                            {u.user_id === user?.user_id ? (
                              <Badge className={getRoleColor(u.role)}>{roles.find(r => r.value === u.role)?.label || u.role}</Badge>
                            ) : (
                              <Select value={u.role || "caller"} onValueChange={(val) => handleUpdateRole(u.user_id, val)}>
                                <SelectTrigger className="w-28 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {roles.map((role) => (
                                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>
                          <TableCell>
                            {/* Callers can be assigned to Team Leads */}
                            {u.role === "caller" && (
                              <Select 
                                value={u.team_lead_id || "none"} 
                                onValueChange={(val) => handleUpdateAllocationSettings(u.user_id, { team_lead_id: val === "none" ? null : val })}
                              >
                                <SelectTrigger className="w-40 h-8">
                                  <SelectValue placeholder="Select Team Lead" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No team lead</SelectItem>
                                  {users.filter(tl => tl.role === "team_lead").map((tl) => (
                                    <SelectItem key={tl.user_id} value={tl.user_id}>
                                      {tl.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            {/* Team Leads can be assigned to Admins */}
                            {u.role === "team_lead" && (
                              <Select 
                                value={u.team_lead_id || "none"} 
                                onValueChange={(val) => handleUpdateAllocationSettings(u.user_id, { team_lead_id: val === "none" ? null : val })}
                              >
                                <SelectTrigger className="w-40 h-8">
                                  <SelectValue placeholder="Select Admin" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No admin</SelectItem>
                                  {users.filter(a => a.role === "admin").map((a) => (
                                    <SelectItem key={a.user_id} value={a.user_id}>
                                      {a.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            {u.role === "admin" && (
                              <Badge variant="outline" className="text-purple-700 border-purple-300">
                                Admin
                              </Badge>
                            )}
                            {u.role === "super_admin" && (
                              <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                                Super Admin
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {!["admin", "super_admin"].includes(u.role) && (
                              <div className="relative">
                                {editingSourcesUser === u.user_id ? (
                                  <div className="flex flex-col gap-2">
                                    <div className="flex flex-wrap gap-1 p-2 border rounded max-w-[200px] max-h-[120px] overflow-y-auto">
                                      {leadSources.map(src => (
                                        <label key={src.source} className="flex items-center gap-1 text-xs cursor-pointer">
                                          <input
                                            type="checkbox"
                                            className="w-3 h-3"
                                            checked={(u.allowed_sources || []).includes(src.source)}
                                            onChange={(e) => {
                                              const current = u.allowed_sources || [];
                                              const updated = e.target.checked
                                                ? [...current, src.source]
                                                : current.filter(s => s !== src.source);
                                              handleUpdateAllowedSources(u.user_id, updated);
                                            }}
                                          />
                                          <span className="capitalize">{src.source?.replace(/_/g, ' ')}</span>
                                        </label>
                                      ))}
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingSourcesUser(null)}>
                                      Done
                                    </Button>
                                  </div>
                                ) : (
                                  <div 
                                    className="cursor-pointer hover:bg-slate-50 p-1 rounded"
                                    onClick={() => setEditingSourcesUser(u.user_id)}
                                  >
                                    {u.allowed_sources && u.allowed_sources.length > 0 ? (
                                      <div className="flex flex-wrap gap-1 max-w-[150px]">
                                        {u.allowed_sources.slice(0, 3).map(src => (
                                          <Badge key={src} variant="secondary" className="text-xs capitalize">
                                            {src?.replace(/_/g, ' ')}
                                          </Badge>
                                        ))}
                                        {u.allowed_sources.length > 3 && (
                                          <Badge variant="outline" className="text-xs">
                                            +{u.allowed_sources.length - 3}
                                          </Badge>
                                        )}
                                      </div>
                                    ) : (
                                      <Badge variant="outline" className="text-xs text-slate-500">
                                        All Sources
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {!["admin", "super_admin"].includes(u.role) && (
                              <Switch
                                checked={u.is_active_for_allocation !== false}
                                onCheckedChange={(checked) => handleUpdateAllocationSettings(u.user_id, { is_active_for_allocation: checked })}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {!["admin", "super_admin"].includes(u.role) && (
                              <Input
                                type="number"
                                className="w-20 h-8"
                                value={u.max_leads_per_day || 50}
                                onChange={(e) => handleUpdateAllocationSettings(u.user_id, { max_leads_per_day: parseInt(e.target.value) || 50 })}
                                min={1}
                                max={500}
                              />
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetPassword(u.user_id)}
                                className="text-orange-600 hover:text-orange-700"
                                title="Reset Password"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              {u.user_id !== user?.user_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleStatus(u.user_id, u.is_active !== false)}
                                  className={u.is_active !== false ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"}
                                  title={u.is_active !== false ? "Deactivate" : "Activate"}
                                >
                                  {u.is_active !== false ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agent Mapping Tab */}
          <TabsContent value="mapping" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-cyan-700" />
                  Privyr Agent Mapping
                </CardTitle>
                <CardDescription>
                  Map agent names from Privyr import to system users. This will assign old leads to the correct agents.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Instructions */}
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium mb-2">How to map agents:</p>
                  <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
                    <li>First, create system users for each Privyr agent in the <strong>Users</strong> tab</li>
                    <li>Select the corresponding system user from the dropdown for each Privyr agent</li>
                    <li>Click <strong>Apply All Mappings to Leads</strong> to bulk-assign leads to agents</li>
                  </ol>
                </div>

                <div className="flex justify-between items-center mb-4">
                  <Button variant="outline" size="sm" onClick={fetchAgentMappings} disabled={loadingMappings}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingMappings ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleApplyAllMappings}
                    disabled={applyingMappings}
                  >
                    {applyingMappings ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4 mr-2" />
                    )}
                    Apply All Mappings to Leads
                  </Button>
                </div>

                {/* Mapping Summary */}
                {!loadingMappings && agentMappings.privyr_agents?.length > 0 && (
                  <div className="flex items-center gap-4 text-sm mb-4 p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">
                      Total Privyr Agents: <strong>{agentMappings.privyr_agents.length}</strong>
                    </span>
                    <span className="text-green-600">
                      Mapped: <strong>{agentMappings.privyr_agents.filter(a => a.mapped_to).length}</strong>
                    </span>
                    <span className="text-orange-600">
                      Unmapped: <strong>{agentMappings.privyr_agents.filter(a => !a.mapped_to).length}</strong>
                    </span>
                    <span className="text-slate-600">
                      Total Leads: <strong>{agentMappings.privyr_agents.reduce((sum, a) => sum + a.lead_count, 0).toLocaleString()}</strong>
                    </span>
                  </div>
                )}

                {loadingMappings ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Privyr Agent Name</TableHead>
                          <TableHead className="text-center">Leads</TableHead>
                          <TableHead className="text-center">
                            <ArrowRight className="w-4 h-4 inline" />
                          </TableHead>
                          <TableHead>Map to System User</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {agentMappings.privyr_agents?.map((agent) => (
                          <TableRow key={agent.name}>
                            <TableCell className="font-medium">{agent.name}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary">{agent.lead_count}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                            </TableCell>
                            <TableCell>
                              <Select 
                                value={agent.mapped_to || ""} 
                                onValueChange={(val) => handleMapAgent(agent.name, val)}
                              >
                                <SelectTrigger className="w-48">
                                  <SelectValue placeholder="Select user..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {agentMappings.system_users?.map((u) => (
                                    <SelectItem key={u.user_id} value={u.user_id}>
                                      {u.name} ({u.role})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {agent.mapped_to ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <Check className="w-3 h-3 mr-1" />
                                  Mapped
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600 border-orange-300">
                                  Not Mapped
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Allocation Settings Tab */}
          <TabsContent value="allocation" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-700" />
                  Today&apos;s Lead Allocation
                </CardTitle>
                <CardDescription>
                  View real-time allocation stats. New leads are distributed using weighted round-robin among active agents.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" onClick={fetchAllocationStats} disabled={loadingStats} className="mb-4">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
                  Refresh Stats
                </Button>

                {loadingStats ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : allocationStats ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                      <span>Date: <strong>{allocationStats.date}</strong></span>
                      <span>Active Agents: <strong>{allocationStats.total_active_agents}</strong></span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {allocationStats.agents?.map((agent) => (
                        <Card key={agent.user_id} className="border-slate-200">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <Avatar className="w-10 h-10">
                                <AvatarFallback className="bg-cyan-100 text-cyan-700">
                                  {agent.name?.charAt(0) || "A"}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{agent.name}</p>
                                <p className="text-xs text-slate-500">{agent.email}</p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Leads Today:</span>
                                <span className="font-semibold">{agent.leads_allocated_today}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Max Capacity:</span>
                                <span className="font-semibold">{agent.max_leads_per_day}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Remaining:</span>
                                <span className={`font-semibold ${agent.remaining_capacity <= 5 ? 'text-red-600' : 'text-green-600'}`}>
                                  {agent.remaining_capacity}
                                </span>
                              </div>
                              {/* Progress bar */}
                              <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                                <div 
                                  className={`h-2 rounded-full ${agent.remaining_capacity <= 5 ? 'bg-red-500' : 'bg-cyan-600'}`}
                                  style={{ width: `${(agent.leads_allocated_today / agent.max_leads_per_day) * 100}%` }}
                                />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {allocationStats.agents?.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <Settings2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No agents are active for allocation</p>
                        <p className="text-sm mt-1">Enable allocation for agents in the Users tab</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No allocation data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create User Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Full Name *</label>
                <Input
                  placeholder="John Doe"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email *</label>
                <Input
                  type="email"
                  placeholder="john@hearclear.in"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Role</label>
                <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {roles.find(r => r.value === newUser.role)?.description}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Password (Optional)</label>
                <Input
                  type="text"
                  placeholder="Leave empty to auto-generate"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                />
                <p className="text-xs text-slate-500 mt-1">
                  If left empty, a temporary password will be generated
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-cyan-700 hover:bg-cyan-800" onClick={handleCreateUser}>
                  Create User
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Temp Password Modal */}
        <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Temporary Password Generated
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800 mb-2">
                  Share this password with <strong>{selectedUser?.email}</strong>
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-white border rounded-lg font-mono text-lg">
                    {tempPassword}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(tempPassword)}
                  >
                    {copiedPassword ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="text-sm text-slate-600">
                <p>• The user will be required to change this password on first login</p>
                <p>• Store this password securely - it will not be shown again</p>
              </div>
              <Button 
                className="w-full bg-cyan-700 hover:bg-cyan-800" 
                onClick={() => { setShowPasswordModal(false); setTempPassword(null); }}
              >
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
